import React from 'react';

interface BannerTilesMainProps {
  title: string;
  subtitle?: string;
  description?: string;
  buttonText?: string;
  buttonUrl?: string;
  backgroundImage?: string;
}

interface BannerTilesListItemProps {
  title: string;
  description: string;
  url?: string;
  backgroundImage?: string;
}

interface BannerTilesProps {
  mainSection: BannerTilesMainProps;
  listItems: BannerTilesListItemProps[];
  className?: string;
}

function BannerTilesListItem({
  title,
  description,
  url,
  backgroundImage,
}: BannerTilesListItemProps) {
  const Tag = url ? 'a' : 'div';
  const props = url ? { href: url } : {};

  return (
    <Tag
      {...props}
      className="au-relative au-flex au-flex-col au-justify-end au-h-full au-group au-overflow-hidden"
    >
      <div className="au-relative au-z-1 au-p-6 md:au-p-9 au-flex au-flex-col au-justify-end au-h-full">
        <h2 className="au-text-[25px] au-mb-5 au-text-white xl2:au-text-[34px] md:au-mb-1">
          {title}
        </h2>
        <p className="au-text-white au-text-[16px] au-pr-4 au-mb-5 md:au-mb-1">
          {description}
        </p>

        {url && (
          <div className="au-absolute au-left-1/2 au-right-1/2 au-bottom-6 md:au-left-auto md:au-right-9 md:au-bottom-9">
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              className="au-text-white"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.55554 1.65625L10.8991 5.99985M10.8991 5.99985L6.55554 10.3434M10.8991 5.99985H1.10059"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
      </div>

      {backgroundImage && (
        <>
          <div className="au-absolute au-inset-0 au-z-[-1]" />
          <div className="au-absolute au-inset-0 au-z-0">
            <img
              src={backgroundImage}
              alt=""
              className="au-w-full au-h-full au-object-cover au-transition-transform au-duration-200 group-hover:au-scale-110"
            />
          </div>
        </>
      )}

      <div className="au-absolute au-bottom-0 au-left-0 au-right-0 au-h-1 au-bg-yellow au-transform au-scale-x-0 group-hover:au-scale-x-100 au-duration-200 au-z-20" />
    </Tag>
  );
}

function BannerTilesMain({
  title,
  subtitle,
  description,
  buttonText,
  buttonUrl,
  backgroundImage,
}: BannerTilesMainProps) {
  return (
    <div className="au-relative au-flex au-flex-col au-justify-end au-min-h-[600px] lg:au-w-1/2">
      <div className="au-relative au-z-2 au-px-6 au-py-14 md:au-px-9 lg:au-px-14 md:au-w-[61%]">
        {subtitle && (
          <p className="au-text-white au-mb-4 au-text-lg">{subtitle}</p>
        )}
        <h1 className="au-text-4xl au-font-bold au-text-white au-mb-6">
          {title}
        </h1>
        {description && (
          <p className="au-text-white au-text-lg au-mb-8">{description}</p>
        )}
        {buttonText && buttonUrl && (
          <a
            href={buttonUrl}
            className="au-inline-flex au-items-center au-px-8 au-py-3 au-bg-yellow hover:au-bg-yellow-500 au-text-black au-text-[14px] au-transition-colors au-font-black"
          >
            {buttonText}
          </a>
        )}
      </div>

      {backgroundImage && (
        <>
          <div className="au-absolute au-inset-0 au-dark-overlay au-z-[1]" />
          <div className="au-absolute au-inset-0 au-z-0">
            <img
              src={backgroundImage}
              alt=""
              className="au-w-full au-h-full au-object-cover"
            />
          </div>
        </>
      )}
    </div>
  );
}

function BannerTiles({
  mainSection,
  listItems,
  className = '',
}: BannerTilesProps) {
  const mainSectionParsed =
    typeof mainSection === 'string' ? JSON.parse(mainSection) : mainSection;
  const listItemParsed =
    typeof listItems === 'string' ? JSON.parse(listItems) : listItems;
  return (
    <div className={`au-flex au-flex-col lg:au-flex-row ${className}`}>
      <BannerTilesMain {...mainSectionParsed} />

      <div className="lg:au-w-1/2 au-grid md:au-grid-cols-2">
        {listItemParsed?.map((item: BannerTilesListItemProps) => (
          <BannerTilesListItem {...item} />
        ))}
      </div>
    </div>
  );
}

BannerTiles.attributes = {
  mainSection: {
    type: 'object',
    default: {
      title: 'Best of AU 2024',
      subtitle: 'Watch the best sessions from AU 2024',
      description: 'Explore curated content from industry leaders and experts.',
      buttonText: 'Watch now',
      buttonUrl: '/watch',
      backgroundImage: '',
    },
  },
  listItems: {
    type: 'array',
    default: [
      {
        title: 'Announcing: AU 2025 in Nashville',
        description:
          'Mark your calendars! AU 2025 will be in Nashville from September 15-18.',
        url: '/au-2025',
        backgroundImage: '',
      },
      {
        title: 'AU 2024 Year-Round Learning',
        description:
          'Gain new skills, explore bold ideas, and hear inspiring stories.',
        url: '/year-round',
        backgroundImage: '',
      },
      {
        title: 'Join the Autodesk Community',
        description:
          'Continue the conversation, learn more, and connect with the Autodesk Community.',
        url: '/community',
        backgroundImage: '',
      },
      {
        title: 'Stay connected',
        description: 'Follow along with updates from AU 2024. Read the blog.',
        url: '/blog',
        backgroundImage: '',
      },
    ],
  },
};

export default BannerTiles;
